import React, {use, useEffect, useRef} from 'react';
import styled, {css} from 'styled-components';
import {Icon} from './Icon';
import {getColor} from '../utils/styled';
import {CURIOUS_BLUE, GRAY_40, GRAY_60, LOCHMARA, MINE_SHAFT, PURE_WHITE,} from '../refs/Colors';
import {FormInputContext} from "../providers/FormInputContext.tsx";
import {InputPath} from "../types/InputPath.ts";
import {ICON_ERASE} from "../refs/Icons.tsx";
import {Tooltip} from "../elements/Tooltip.tsx";
import {useDictationPatch} from "../utils/useDictationPatch.ts";

type TextInputProps = {
  className?: string;
  size: number;
  placeholder?: string;
  disabled?: boolean;
  multiline?: boolean;
  autoResize?: boolean;
  type?: string;
  iconLeft?: string;
  rightIcon?: string;
  rightText?: string;
  rightHref?: string;
  onRightClick?: () => void;
  path: InputPath<string>;
  withEraser?: boolean;
};

type SizeStyleMap = Record<number, ReturnType<typeof css>>;

const SIZE_STYLES: SizeStyleMap = {
  36: css`
    min-height: 36px;
    padding: 8px 36px 8px 13px;
    font-size: 14px;
  `,
  30: css`
    min-height: 30px;
    padding: 2px 22px 2px 10px;
    font-size: 12px;
  `,
};

const Wrapper = styled.div<{ $size: number, $hasRightIcon: boolean, $multiline?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${getColor(GRAY_40)};
  border-radius: 5px;
  background-color: ${getColor(PURE_WHITE)};
  transition-property: color, border-color, box-shadow;
  transition-duration: 0.3s;

  &:focus-within {
    outline: none;
  }

  &:hover {
    border-color: ${getColor(CURIOUS_BLUE)};
  }

  &:focus-within {
    border-color: ${getColor(CURIOUS_BLUE)};
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
  }

  ${props => {
    const styles = SIZE_STYLES[props.$size];
    if (!styles) {
      throw new Error(`Unsupported TextInput size: ${props.$size}`);
    }
    return styles;
  }}

  ${props => props.onClick && 'cursor: text;'}

  ${props => props['aria-disabled'] && `
    opacity: 0.5;
    pointer-events: none;
  `}

  ${props => props.$hasRightIcon && !props.$multiline && `
    .textinput-right-icon {
      margin-right: -10px;
    }
  `}
  ${props => props.$hasRightIcon && props.$multiline && `
    display: block;
    position: relative;
    padding-bottom: 28px;
    padding-right: 10px;
    .textinput-right-icon {
      position: absolute;
      left: auto;
      top: auto;
      right: 9px;
      bottom: 10px;
      align-self: flex-end;
    }    
  `}
`;

const Input = styled.input`
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  /* Override global input styles */
  && {
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    margin: 0;
    font-size: inherit;
    border-radius: 0;
    overflow: hidden;
  }
  &&:focus {
    box-shadow: none;
  }

  color: ${getColor(MINE_SHAFT)};
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  line-height: normal;

  &::placeholder {
    color: ${getColor(GRAY_60)};
  }

`;

const TextArea = styled.textarea`
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 5px;
  box-sizing: border-box;
  resize: none;

  /* Override global textarea styles */
  && {
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    margin: 0;
    font-size: inherit;
    border-radius: 0;
    overflow: auto;
  }
  &&:focus {
    box-shadow: none;
  }

  color: ${getColor(MINE_SHAFT)};
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  line-height: normal;

  &::placeholder {
    color: ${getColor(GRAY_60)};
  }
`;

const RightText = styled.a`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: normal;
  color: ${getColor(MINE_SHAFT)};
  text-decoration: underline;
  text-decoration-skip-ink: none;
  cursor: pointer;
`;

export const TextInput: React.FC<TextInputProps> = ({
  className,
  size,
  placeholder,
  disabled,
  multiline,
  autoResize,
  type,
  iconLeft,
  rightIcon,
  rightText,
  rightHref,
  onRightClick,
  path,
  withEraser,
  ...rest
}) => {
  const rightTextProps = rightHref
    ? { href: rightHref, target: '_blank', rel: 'noreferrer' }
    : {};

  const useInput = use(FormInputContext)
  const dictationPatch = useDictationPatch()
  const [value,setValue] = useInput(path,rest)
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValue(e.target.value);
  }
  const InputComponent: React.ElementType = multiline ? TextArea : Input;
  useEffect(() => {
    if (!multiline || !autoResize || !textAreaRef.current) {
      return;
    }
    const el = textAreaRef.current;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [multiline, autoResize, value]);

  const hasRightIcon = withEraser ? !!value : !!rightIcon;

  return (
    <Wrapper $multiline={multiline} $hasRightIcon={hasRightIcon} className={className} $size={size} aria-disabled={disabled}>
      {iconLeft && <Icon icon={iconLeft} size={24} />}
      <InputComponent
        {...(!multiline ? { type } : {})}
        value={value}
        {...dictationPatch}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
        ref={multiline ? textAreaRef : undefined}
      />
      {rightText && (
        <RightText as={rightHref ? 'a' : 'span'} onClick={onRightClick} {...rightTextProps}>
          {rightText}
        </RightText>
      )}
      {
        withEraser
        ? (value &&
            <Tooltip.Trigger
              className="textinput-right-icon"
              $lineHeight={1} tooltip={<Tooltip my="bottom center" at="top center">Erase text</Tooltip>}
            >
              <Icon icon={ICON_ERASE} color={LOCHMARA} size={16} clickable onClick={() => setValue('')} />
            </Tooltip.Trigger>
          )
        : <Icon className="textinput-right-icon" icon={rightIcon} color={LOCHMARA} size={16} clickable={!!onRightClick} onClick={onRightClick} />
      }
    </Wrapper>
  );
};

export default TextInput;
